import React, {useState} from 'react';
import ReactApexChart from 'react-apexcharts';
import {Dropdown} from 'react-bootstrap';

const MonthlyChart = ({data}) => {
    const [chartType, setChartType] = useState('stacked');
    const [chartDisplay, setChartDisplay] = useState('Stacked Bars');

    const chartTypes = [
        {id: 'stacked', name: 'Stacked Bars', icon: 'bi-layers'},
        {id: 'grouped', name: 'Grouped Bars', icon: 'bi-bar-chart'},
        {id: 'line', name: 'Line Chart', icon: 'bi-graph-up'},
        {id: 'area', name: 'Area Chart', icon: 'bi-area-chart'}
    ];

    // Solid color definitions (no gradients)
    const solidColors = ['#4e73df', '#1cc88a', '#e74a3b'];

    const series = [
        {
            name: 'Sent',
            data: data.map((row) => row.sent)
        },
        {
            name: 'Received',
            data: data.map((row) => row.received)
        },
        {
            name: 'Failed',
            data: data.map((row) => row.failed)
        }
    ];

    const baseOptions = {
        chart: {
            height: 350,
            toolbar: {show: false},
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800
            }
        },
        colors: solidColors, // Using solid colors
        dataLabels: {enabled: false},
        stroke: {
            width: 2,
            curve: 'smooth'
        },
        xaxis: {
            categories: data.map((row) => row.month),
            labels: {
                rotate: -45,
                style: {fontSize: '12px', colors: '#6c757d'}
            },
            axisBorder: {show: true, color: '#e9ecef'},
            axisTicks: {show: true, color: '#e9ecef'}
        },
        yaxis: {
            title: {
                text: 'Messages',
                style: {fontWeight: 'normal', color: '#6c757d', fontSize: '12px'}
            },
            labels: {style: {colors: '#6c757d'}},
            min: 0
        },
        grid: {
            borderColor: '#f8f9fa',
            strokeDashArray: 3
        },
        fill: {
            opacity: 1, // Solid fill with no transparency
            type: 'solid' // Explicitly set to solid (no gradient)
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            y: {formatter: (val) => `${val} messages`}
        },
        legend: {
            position: 'top',
            horizontalAlign: 'center',
            markers: {width: 12, height: 12, radius: 6}
        }
    };

    const getChartOptions = () => {
        switch (chartType) {
            case 'stacked':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'bar', stacked: true},
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            borderRadius: 6,
                            borderRadiusApplication: 'end',
                            columnWidth: '60%'
                        }
                    }
                };
            case 'grouped':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'bar', stacked: false},
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            borderRadius: 6,
                            borderRadiusApplication: 'end',
                            columnWidth: '60%'
                        }
                    }
                };
            case 'line':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'line'},
                    markers: {size: 5},
                    stroke: {width: 3} // Slightly thicker lines for better visibility
                };
            case 'area':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'area', stacked: true},
                    fill: {
                        type: 'solid', // Changed from gradient to solid
                        opacity: 0.7 // Slightly transparent for area charts
                    }
                };
            default:
                return baseOptions;
        }
    };

    return (
        <div className="card shadow h-100 border-0">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                <div className="card-title mb-0">
                    <i className="bi bi-calendar-month text-primary me-2"></i>
                    Monthly Message Report
                </div>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" size="sm" id="chart-type-dropdown">
                        <i className={`bi ${chartTypes.find(t => t.id === chartType)?.icon} me-2`}></i>
                        {chartDisplay}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        {chartTypes.map((type) => (
                            <Dropdown.Item
                                key={type.id}
                                active={chartType === type.id}
                                onClick={() => {
                                    setChartType(type.id);
                                    setChartDisplay(type.name);
                                }}
                            >
                                <i className={`bi ${type.icon} me-2`}></i>
                                {type.name}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className="card-body p-0">
                <ReactApexChart
                    options={getChartOptions()}
                    series={series}
                    type={chartType === 'line' || chartType === 'area' ? chartType : 'bar'}
                    height={350}
                    width="100%"
                />
            </div>

            <div className="card-footer bg-white border-0 small text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Hover over chart elements to see detailed message counts
            </div>
        </div>
    );
};

export default MonthlyChart;